import { Button } from "@/components/ui/button";
import { Package, Plus, Users, ClipboardList } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from 'react-i18next';

interface ProjectActionsProps {
  onNewProject: () => void;
}

export const ProjectActions = ({ onNewProject }: ProjectActionsProps) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  return (
    <div className="glass-card p-5 border border-white/5 rounded-xl backdrop-blur-md shadow-lg">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-5">
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <div className="p-3 rounded-lg bg-primary/20 backdrop-blur-md">
            <ClipboardList className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold text-white">{t('projects.your_projects')}</h2>
            <p className="text-sm text-white/60">{t('projects.create_and_manage')}</p>
          </div>
        </div>
        
        {/* Mobile: Vertical stack */}
        <div className="flex-col gap-3 w-full hidden sm:hidden">
          <Button 
            onClick={onNewProject}
            className="w-full transition-all hover:shadow-lg bg-primary hover:bg-primary/90 font-brakle mb-2"
          >
            <Plus className="mr-2 h-4 w-4 shrink-0" />
            {t('projects.new_project')}
          </Button>
          <div className="flex gap-2 w-full">
            <Button 
              onClick={() => navigate("/inventory")}
              variant="outline"
              className="flex-1 transition-all hover:shadow-lg hover:border-primary/50 glass-card font-brakle"
            >
              <Package className="mr-2 h-4 w-4 shrink-0" />
              {t('inventory_tab')}
            </Button>
            <Button 
              onClick={() => navigate("/contacts")}
              variant="outline"
              className="flex-1 transition-all hover:shadow-lg hover:border-primary/50 glass-card font-brakle"
            >
              <Users className="mr-2 h-4 w-4 shrink-0" />
              {t('crew')}
            </Button>
          </div>
        </div>
        
        {/* Desktop: Row */}
        <div className="flex gap-3 w-full sm:w-auto">
          <Button 
            onClick={() => navigate("/inventory")}
            variant="outline"
            className="flex-1 sm:flex-none transition-all hover:shadow-lg hover:border-primary/50 glass-card font-brakle"
          >
            {isMobile ? (
              <Package className="h-4 w-4" />
            ) : (
              <>
                <Package className="mr-2 h-4 w-4 shrink-0" />
                {t('inventory_tab')}
              </>
            )}
          </Button>
          <Button 
            onClick={() => navigate("/contacts")}
            variant="outline"
            className="flex-1 sm:flex-none transition-all hover:shadow-lg hover:border-primary/50 glass-card font-brakle"
          >
            {isMobile ? (
              <Users className="h-4 w-4" />
            ) : (
              <>
                <Users className="mr-2 h-4 w-4 shrink-0" />
                {t('crew')}
              </>
            )}
          </Button>
          <Button 
            onClick={onNewProject}
            className="flex-1 sm:flex-none transition-all hover:shadow-lg bg-primary hover:bg-primary/90 font-brakle"
          >
            {isMobile ? (
              <Plus className="h-4 w-4" />
            ) : (
              <>
                <Plus className="mr-2 h-4 w-4 shrink-0" />
                {t('projects.new_project')}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
